import React, { useEffect, Dispatch, SetStateAction } from "react";

import { EtyUser } from "../../interfaces/entities";
import { IUserData } from "../../interfaces/forms";
import { ISelectDropdownOption } from "../../interfaces/form_elements";

import { Icons } from "../../data/Assets";
import { OPUserRoles, OPUserStatuses } from "../../data/options";

import { getFormattedString, getPhoneNumberString } from "../../helpers/utility";

import { SelectDropdown, TextInput } from "../reusables/TableInputs";
import { SVGIcon } from "../wrappers/SVGIcon";

interface IUsersTableProps {
    organizationId: number;
    usersRes: EtyUser[];
    isLoading: boolean;
    users: IUserData[];
    setUsers: Dispatch<SetStateAction<IUserData[]>>;
    originalUsers: IUserData[];
    setOriginalUsers: Dispatch<SetStateAction<IUserData[]>>;
    onInviteUsers: (user: IUserData | null) => void;
    organizationStatus: string | undefined;
    isOkToInvite: (from: string) => boolean;
};
type TUserField = 'name' | 'status' | 'email' | 'verified' | 'role' | 'phone';
const UsersTable = (props: IUsersTableProps) => {
    const { organizationId, usersRes, isLoading, users, setUsers, setOriginalUsers, onInviteUsers, isOkToInvite } = props;

    useEffect(() => {
        if (!usersRes.length) return;

        const tempUsers: IUserData[] = usersRes.map(user => {
            return {
                id: user.id,
                organization_id: organizationId,
                name: user.name,
                status: user.status,
                email: user.email,
                verified: user.verified,
                role: user.role,
                phone: getPhoneNumberString(user.phone ?? ''),
            };
        });

        setUsers(tempUsers);
        setOriginalUsers(tempUsers);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usersRes]);

    const isAddNewDisabled = () => {
        const lastUser = users[users.length - 1];

        if (!lastUser) return false;
        if (lastUser.id) return false;
        if (!lastUser.name?.trim()?.length || !lastUser.email?.trim()?.length) return true;
    };

    const onAddNewUser = () => {
        if (isAddNewDisabled()) return;

        setUsers(prevData => {
            const newData = [...prevData];
            newData.push({
                id: null,
                organization_id: organizationId,
                name: '',
                status: 'active',
                email: '',
                verified: false,
                role: 'staff',
                phone: ''
            });

            return newData;
        });
    };

    const onTextChange = (fieldName: TUserField, value: string, index: number) => {
        setUsers(prevData => {
            const newData = [...prevData];

            if (newData[index]) {
                newData[index] = { ...newData[index], [fieldName]: value };
            };

            return newData;
        });
    };

    const onSelectOption = (fieldName: TUserField, option: ISelectDropdownOption, index: number) => {
        setUsers(prevData => {
            const newData = [...prevData];

            if (newData[index]) {
                newData[index] = { ...newData[index], [fieldName]: option.value };
            };

            return newData;
        });
    };

    return (
        <div className="immployer_admin__table_container__wrapper">
            <div className="immployer_admin__table_container">
                <table className="immployer_admin__table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Status</th>
                            <th>Email</th>
                            <th>Verified</th>
                            <th>Role</th>
                            <th>Phone</th>
                            <th>Invite Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            isLoading
                                ? <tr><td colSpan={3}>Loading...</td></tr>
                                : !users.length
                                    ? <tr><td colSpan={3}>No Users yet.</td></tr>
                                    : users.map((user, index) => {
                                        return (
                                            <tr key={index}>
                                                <td className="immployer_admin__input_cell min_wdth_30 max_wdth_30">
                                                    <TextInput
                                                        name='name'
                                                        value={user.name ?? ''}
                                                        onChange={(value) => onTextChange('name', value, index)}
                                                        placeholder="Enter name"
                                                        max_length={100}
                                                    />
                                                </td>
                                                <td className="immployer_admin__input_cell min_wdth_13 max_wdth_13">
                                                    <SelectDropdown
                                                        name='status'
                                                        value={{ value: user.status, label: getFormattedString(user.status, 'title_case') }}
                                                        onChange={(option) => onSelectOption('status', option, index)}
                                                        options={OPUserStatuses}
                                                        placeholder="-- Select Status --"
                                                    />
                                                </td>
                                                <td className="immployer_admin__input_cell min_wdth_30 max_wdth_30">
                                                    <TextInput
                                                        name='email'
                                                        value={user.email ?? ''}
                                                        onChange={(value) => onTextChange('email', value, index)}
                                                        placeholder="Enter email"
                                                        max_length={100}
                                                        read_only={user.verified}
                                                    />
                                                </td>
                                                <td>
                                                    {
                                                        user.verified
                                                            ? <SVGIcon src={Icons.CheckCircleFill} color="success" />
                                                            : <SVGIcon src={Icons.DotsCircleFill} color="primary" />
                                                    }
                                                </td>
                                                <td className="immployer_admin__input_cell min_wdth_13 max_wdth_13">
                                                    <SelectDropdown
                                                        name='role'
                                                        value={{ value: user.role, label: getFormattedString(user.role, 'title_case') }}
                                                        onChange={(option) => onSelectOption('role', option, index)}
                                                        options={OPUserRoles}
                                                        placeholder="-- Select Role --"
                                                    />
                                                </td>
                                                <td className="immployer_admin__input_cell min_wdth_17 max_wdth_17">
                                                    <TextInput
                                                        name='phone'
                                                        value={user.phone ?? ''}
                                                        onChange={(value) => onTextChange('phone', value, index)}
                                                        placeholder="Enter phone"
                                                        max_length={12}
                                                        pattern_mask="phone_number"
                                                    />
                                                </td>
                                                <td>
                                                    <button
                                                        className={`immployer_admin__btn immployer_admin__btn__primary ${user.verified || !user.id || !isOkToInvite('individual') ? 'immployer_admin__btn__primary__disabled' : ''}`}
                                                        onClick={() => user.verified || !user.id || !isOkToInvite('individual') ? null : onInviteUsers(user)}
                                                    >
                                                        Invite
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    })
                        }
                    </tbody>
                </table>
            </div>
            <div className="immployer_admin__table__actions">
                <button
                    className={`immployer_admin__table__add_new_btn ${isAddNewDisabled() ? 'immployer_admin__table__add_new_btn__disabled' : ''}`}
                    onClick={onAddNewUser}
                >
                    <SVGIcon src={Icons.PlusCircleFill} />
                    <span>Add new User</span>
                </button>
            </div>
        </div>
    );
};

export default UsersTable;